import { AIRANPlatform, DuplexMode, FrequencyRange, FRONTHAUL_TYPE_CONFIG, FronthaulType, GnbInstance, LabKitPlatform, MachineDto, ModalService, OaiboxType, ProductDetailsDto, RanMode, SimpleMessageService, Tenant, WebAppCommonModule } from "@allbesmart/web-app-common";
import { UserRole } from "@allbesmart/web-app-keycloak-auth";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import Keycloak from "keycloak-js";
import { Subscription } from "rxjs";
import { v4 } from "uuid";
import { defaultRanMode, isAiRanType } from "../../helpers/oaibox-type";
import { GnbInstanceConfigurationFormGroup } from "../../models/gnb-instance-config-form-group.model";
import { TenantManagementService } from "../../services/tenant-management.service";
import { MachineSerialNumberUpdateModalComponent } from "../machine-serial-number-update-modal/machine-serial-number-update-modal.component";
import { GnbInstanceConfigTableComponent } from "../../shared/components/gnb-instance-config-table/gnb-instance-config-table.component";

@Component({
    selector: "oaibox-update-machine-configuration-modal",
    templateUrl: "./update-machine-configuration-modal.component.html",
    styleUrls: ["./update-machine-configuration-modal.component.css"],
    imports: [
        WebAppCommonModule,
        ReactiveFormsModule,
        GnbInstanceConfigTableComponent,
    ],
})
export class UpdateMachineConfigurationModalComponent
  implements OnInit, OnDestroy
{
  @Input() tenant?: Tenant;
  @Input({ required: true }) machine!: MachineDto;

  protected readonly AVAILABLE_OAIBOX_TYPES: OaiboxType[] =
    Object.values(OaiboxType);
  protected readonly AVAILABLE_AIRAN_PLATFORMS;
  protected readonly AVAILABLE_LAB_KIT_PLATFORMS;
  protected readonly AVAILABLE_RAN_MODES: RanMode[] = Object.values(RanMode);
  protected readonly AVAILABLE_FRONTHAUL_TYPES: FronthaulType[] =
    Object.values(FronthaulType);
  protected readonly FRONTHAUL_TYPE_CONFIG = FRONTHAUL_TYPE_CONFIG;
  protected readonly AVAILABLE_FREQUENCY_RANGES: FrequencyRange[] =
    Object.values(FrequencyRange);
  protected readonly AVAILABLE_DUPLEX_MODES: DuplexMode[] =
    Object.values(DuplexMode);
  protected readonly FrequencyRange = FrequencyRange;
  protected readonly OaiboxType = OaiboxType;

  public isRoot?: boolean;

  protected updateMachineForm: UpdateMachineFormGroup;

  private machineProductDetailsUpdateSubscription?: Subscription;
  private readonly refreshMachineSubscription?: Subscription;
  private readonly fetchMachineSubscription?: Subscription;

  constructor(
    private readonly modalService: ModalService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly tenantManagementService: TenantManagementService,
    keycloak: Keycloak
  ) {
    this.AVAILABLE_AIRAN_PLATFORMS = Object.values(AIRANPlatform);
    this.AVAILABLE_AIRAN_PLATFORMS.sort();
    this.AVAILABLE_LAB_KIT_PLATFORMS = Object.values(LabKitPlatform);
    this.AVAILABLE_LAB_KIT_PLATFORMS.sort();
    this.isRoot = keycloak.hasRealmRole(UserRole.ROOT);

    this.updateMachineForm = new FormGroup({
      oaiboxType: new FormControl<OaiboxType | null>(null),
      platform: new FormControl<AIRANPlatform | LabKitPlatform | null>(null),
      ranMode: new FormControl<RanMode | null>(null),
      subtypes: new FormGroup({
        cn5g: new FormControl(false),
        flexric: new FormControl(false),
        xApp: new FormControl(false),
        gnbs: new FormArray<GnbInstanceConfigurationFormGroup>([]),
        dus: new FormArray<GnbInstanceConfigurationFormGroup>([]),
        cus: new FormArray<GnbInstanceConfigurationFormGroup>([]),
        cusUP: new FormArray<GnbInstanceConfigurationFormGroup>([]),
        cusCP: new FormArray<GnbInstanceConfigurationFormGroup>([]),
      }),
      fronthaulType: new FormControl<FronthaulType | null>(null),
      frequencyRange: new FormControl<FrequencyRange | null>(null),
      duplexMode: new FormControl<DuplexMode | null>(null),
      ptpConfiguration: new FormGroup({
        interfaceName: new FormControl<string | null>(null),
        enabled: new FormControl<boolean | null>(false),
      }),
      rfSimulatorEnabled: new FormControl<boolean | null>(false),
    });
  }

  ngOnInit(): void {
    const pd = this.machine.productDetails;

    this.updateMachineForm.patchValue({
      oaiboxType: pd.oaiboxType,
      platform: pd.platform ?? null,
      ranMode: pd.ranMode ?? null,
      fronthaulType: pd.fronthaulType ?? null,
      frequencyRange: pd.frequencyRange ?? null,
      duplexMode: pd.duplexMode ?? null,
      ptpConfiguration: {
        interfaceName: pd.ptpConfiguration?.interfaceName ?? null,
        enabled: pd.ptpConfiguration?.enabled ?? false,
      },
      rfSimulatorEnabled: pd.rfSimulatorEnabled ?? false,
    });

    if (pd.oaiboxSubTypes) {
      const subtypes = this.updateMachineForm.controls.subtypes;
      subtypes.patchValue({
        cn5g: pd.oaiboxSubTypes.cn5g ?? false,
        flexric: pd.oaiboxSubTypes.flexric ?? false,
        xApp: pd.oaiboxSubTypes.xApp ?? false,
      });

      pd.oaiboxSubTypes.gnbs?.forEach((gnb) =>
        subtypes.controls.gnbs.push(this.createGnbInstanceConfig(gnb))
      );
      pd.oaiboxSubTypes.dus?.forEach((du) =>
        subtypes.controls.dus.push(this.createGnbInstanceConfig(du))
      );

      pd.oaiboxSubTypes.cus?.forEach((cu) =>
        subtypes.controls.cus.push(this.createComponentConfig(cu))
      );
      pd.oaiboxSubTypes.cusCPlane?.forEach((cuCP) =>
        subtypes.controls.cusCP.push(this.createComponentConfig(cuCP))
      );
      pd.oaiboxSubTypes.cusUPlane?.forEach((cuUP) =>
        subtypes.controls.cusUP.push(this.createComponentConfig(cuUP))
      );
    }
  }

  protected createComponentConfig(
    instance?: GnbInstance
  ): GnbInstanceConfigurationFormGroup {
    return new FormGroup({
      id: new FormControl<string>(instance ? instance.id : v4()),
      fronthaulType: new FormControl<FronthaulType | null>(
        instance ? (instance.fronthaulType ?? null) : null
      ),
      frequencyRange: new FormControl<FrequencyRange | null>(
        instance ? (instance.frequencyRange ?? null) : null
      ),
      duplexMode: new FormControl<DuplexMode | null>(
        instance ? (instance.duplexMode ?? null) : null
      ),
      rfSimulatorEnabled: new FormControl<boolean | null>(
        instance ? (instance.rfSimulatorEnabled ?? false) : false
      ),
      ranMode: new FormControl<RanMode | null>(
        instance?.ranMode ??
          defaultRanMode(this.updateMachineForm.value.oaiboxType),
        [Validators.required]
      ),
    });
  }

  protected createGnbInstanceConfig(
    instance?: GnbInstance
  ): GnbInstanceConfigurationFormGroup {
    return new FormGroup({
      id: new FormControl<string>(instance ? instance.id : v4()),
      fronthaulType: new FormControl<FronthaulType | null>(
        instance ? (instance.fronthaulType ?? null) : null,
        [Validators.required]
      ),
      frequencyRange: new FormControl<FrequencyRange | null>(
        instance ? (instance.frequencyRange ?? null) : null,
        [Validators.required]
      ),
      duplexMode: new FormControl<DuplexMode | null>(
        instance ? (instance.duplexMode ?? null) : null,
        [Validators.required]
      ),
      rfSimulatorEnabled: new FormControl<boolean | null>(
        instance ? (instance.rfSimulatorEnabled ?? false) : false,
        [Validators.required]
      ),
      ranMode: new FormControl<RanMode | null>(
        instance?.ranMode ??
          defaultRanMode(this.updateMachineForm.value.oaiboxType),
        [Validators.required]
      ),
    });
  }

  protected addGnb(): void {
    this.updateMachineForm.controls.subtypes.controls.gnbs.push(
      this.createGnbInstanceConfig()
    );
  }

  protected removeGnb(): void {
    const gnbs = this.updateMachineForm.controls.subtypes.controls.gnbs;
    if (gnbs.length > 0) gnbs.removeAt(gnbs.length - 1);
  }

  protected addDu(): void {
    this.updateMachineForm.controls.subtypes.controls.dus.push(
      this.createGnbInstanceConfig()
    );
  }

  protected removeDu(): void {
    const dus = this.updateMachineForm.controls.subtypes.controls.dus;
    if (dus.length > 0) dus.removeAt(dus.length - 1);
  }

  protected openMachineSerialNumberUpdate(): void {
    this.modalService.pushModal({
      component: MachineSerialNumberUpdateModalComponent,
      inputs: new Map<string, any>([
        ["tenant", this.tenant],
        ["machine", this.machine],
      ]),
      size: "small",
      responsiveWidth: true,
    });
  }

  private toGnbInstances(
    instances: FormArray<GnbInstanceConfigurationFormGroup>
  ): GnbInstance[] {
    return instances.controls.map((instance) => ({
      id: instance.value.id!,
      fronthaulType: instance.value.fronthaulType ?? undefined,
      frequencyRange: instance.value.frequencyRange ?? undefined,
      duplexMode: instance.value.duplexMode ?? undefined,
      rfSimulatorEnabled: instance.value.rfSimulatorEnabled ?? undefined,
      ranMode: instance.value.ranMode ?? undefined,
    }));
  }

  protected saveConfiguration(): void {
    if (!this.tenant || this.updateMachineForm.invalid) return;

    const {
      oaiboxType,
      platform,
      ranMode,
      ptpConfiguration,
      fronthaulType,
      frequencyRange,
      duplexMode,
    } = this.updateMachineForm.value;
    if (!oaiboxType) return;

    const subtypes = this.updateMachineForm.controls.subtypes.controls;
    const productDetailsDto: ProductDetailsDto = {
      oaiboxType,
      platform: platform ?? undefined,
      ranMode:
        oaiboxType === OaiboxType.LAB_KIT ? (ranMode ?? undefined) : undefined,
      oaiboxSubTypes: isAiRanType(oaiboxType)
        ? {
            cn5g: subtypes.cn5g.value ?? undefined,
            flexric: subtypes.flexric.value ?? undefined,
            xApp: subtypes.xApp.value ?? undefined,
            gnbs:
              subtypes.gnbs.length > 0
                ? this.toGnbInstances(subtypes.gnbs)
                : undefined,
            dus:
              subtypes.dus.length > 0
                ? this.toGnbInstances(subtypes.dus)
                : undefined,
            cus:
              subtypes.cus.length > 0
                ? this.toGnbInstances(subtypes.cus)
                : undefined,
            cusUPlane:
              subtypes.cusUP.length > 0
                ? this.toGnbInstances(subtypes.cusUP)
                : undefined,
            cusCPlane:
              subtypes.cusCP.length > 0
                ? this.toGnbInstances(subtypes.cusCP)
                : undefined,
          }
        : undefined,
      fronthaulType: (oaiboxType !== OaiboxType.OAIBOX_CU
        ? (fronthaulType ?? undefined)
        : FronthaulType.NONE) as FronthaulType,
      frequencyRange: (frequencyRange ?? undefined) as FrequencyRange,
      duplexMode: (duplexMode ?? undefined) as DuplexMode,
      ptpConfiguration: {
        interfaceName: ptpConfiguration?.interfaceName ?? "",
        enabled: ptpConfiguration?.enabled ?? false,
      },
      rfSimulatorEnabled:
        this.updateMachineForm.value.rfSimulatorEnabled ?? false,
    };

    this.machineProductDetailsUpdateSubscription?.unsubscribe();
    this.machineProductDetailsUpdateSubscription = this.tenantManagementService
      .updateMachineProductDetails(
        this.tenant.id,
        this.machine.id,
        productDetailsDto
      )
      .subscribe({
        next: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Machine configuration updated successfully!",
            icon: "fa-solid fa-check-double",
            color: "success",
          });
          this.tenantManagementService.refreshTenants();
          this.modalService.popModal();
        },
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Machine configuration failed to be updated!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
          this.modalService.popModal();
        },
      });
  }

  protected cancel(): void {
    this.modalService.popModal();
  }

  ngOnDestroy(): void {
    this.machineProductDetailsUpdateSubscription?.unsubscribe();
    this.refreshMachineSubscription?.unsubscribe();
    this.fetchMachineSubscription?.unsubscribe();
  }
}

type UpdateMachineFormGroup = FormGroup<{
  oaiboxType: FormControl<OaiboxType | null>;
  platform: FormControl<AIRANPlatform | LabKitPlatform | null>;
  ranMode: FormControl<RanMode | null>;
  subtypes: FormGroup<{
    cn5g: FormControl<boolean | null>;
    flexric: FormControl<boolean | null>;
    xApp: FormControl<boolean | null>;
    gnbs: FormArray<GnbInstanceConfigurationFormGroup>;
    dus: FormArray<GnbInstanceConfigurationFormGroup>;
    cus: FormArray<GnbInstanceConfigurationFormGroup>;
    cusUP: FormArray<GnbInstanceConfigurationFormGroup>;
    cusCP: FormArray<GnbInstanceConfigurationFormGroup>;
  }>;
  fronthaulType: FormControl<FronthaulType | null>;
  frequencyRange: FormControl<FrequencyRange | null>;
  duplexMode: FormControl<DuplexMode | null>;
  ptpConfiguration: FormGroup<{
    interfaceName: FormControl<string | null>;
    enabled: FormControl<boolean | null>;
  }>;
  rfSimulatorEnabled: FormControl<boolean | null>;
}>;
